import React, { ReactNode, useCallback, useContext, useMemo } from 'react'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import { OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import { getEffectiveBundledWithFlightsOnly, getEffectiveCharterBundleRoute, getOfferDestinationAirport } from 'selectors/offerPage/offerPageSelectors'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import moment from 'moment'
import { FLIGHTS_MAX_LEAD_TIME_DAYS, FlightsClassTypes } from 'constants/flight'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import GeoContext from 'contexts/geoContext'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Pill from 'LuxKit/Pill'
import OfferBookButton from '../OfferBookButtons/OfferBookButton'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import { isDepositAvailableOnOffer, getDepositAmountPercentage } from 'lib/payment/depositsUtils'
import { isFlexibleCancellationPolicy } from 'lib/offer/cancellationPolicyUtils'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Clickable from 'components/Common/Clickable/Clickable'
import ReserveForZeroPolicyModal from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroPolicyModal'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import config from 'constants/config'
import Group from 'components/utils/Group'
import { max, sortBy, unique } from 'lib/array/arrayUtils'
import ModalContext from 'contexts/ModalContext'
import ReserveForZeroAndDepositPolicyModal from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroAndDepositPolicyModal'
import LineClockDollarIcon from 'LuxKit/Icons/line/LineClockDollarIcon'
import { pluralizeToString } from 'lib/string/pluralize'
import TextLink from 'LuxKit/TextLink'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import FlightDealLabel from 'components/OfferPage/FlightDeal/FlightDealLabel'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import { addFlightPrice, feesToObject } from 'lib/offer/pricing'
import FlashHotelMarginText from './FlashHotelMarginText'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import OfferAddToCartButton from '../OfferBookButtons/OfferAddToCartButton'
import OfferSelectPackageButton from '../OfferBookButtons/OfferSelectPackageButton'
import * as Analytics from 'analytics/analytics'
import { offerPageCheckAvailabilityEvent } from 'analytics/snowplow/events'
import { generateLoyaltyPointsCalculatorBundledFlightOptions, generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { getPriceDisplayFunction } from 'lib/offer/offerPageUtil'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { LUX_PLUS_MONTH_TAKEOVER_ID } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import AgentHubAdditionalCommissionLabel from 'agentHub/components/AgentHubAdditionalCommissionLabel'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { formatGuestsDisplay } from 'lib/string/stringUtils'
import styled from 'styled-components'
import Caption from 'LuxKit/Typography/Caption'
import { useCharterBundleFallbackPricing } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useCharterBundleFallbackPricing'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'
import { useIntl } from 'react-intl'

const FlexAutoGroup = styled(Group)`
  & > * {
    flex: auto;
  }
`

interface Props {
  offer: App.Offer
  roomsBeforeSurcharge?: number
  packageView: App.OfferPackageOptionView
  flightOrigin?: App.Airport
  flightDestination?: App.Airport
  rooms: Array<OfferPageRoom>
  checkIn?: string
  fetching?: boolean
  isSpoofed: boolean
  isStoreMode: boolean
  defaultDepositAmountPercentage: number
  isFlightsSelected: boolean
  charterBundleRoute?: App.CharterBundleRoute
  priceMode: OfferPagePriceMode
  fareClass: FlightsClassTypes
  multipleRoomBookingActive: boolean
  bundledWithFlightsOnly: boolean
}

function FlashHotelOptionPriceDetails(props: Props) {
  const {
    offer,
    roomsBeforeSurcharge,
    flightOrigin,
    flightDestination,
    rooms,
    checkIn,
    fetching,
    isSpoofed,
    isStoreMode,
    defaultDepositAmountPercentage,
    isFlightsSelected,
    packageView,
    charterBundleRoute,
    priceMode,
    fareClass,
    multipleRoomBookingActive,
    bundledWithFlightsOnly,
  } = props

  const intl = useIntl()
  const {
    package: pkg,
    flightsAvailable,
    pricing,
    memberPricing,
  } = packageView
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const { totalFlightPrice: flightPrice, loading: flightPriceLoading } = useOfferPageFlightPrice()

  // For regular (non-charter) flights, use cached flight price from offer as fallback when no dates selected
  const hotelOffer = offer as App.HotelOffer
  const cachedFlightPrice = flightOrigin?.code ? (hotelOffer.flightPrices?.[flightOrigin.code] ?? 0) : 0
  const shouldUseCachedFlightPrice = !charterBundleRoute && !checkIn && !hotelOffer.flightCacheDisabled && cachedFlightPrice > 0
  const effectiveFlightPrice = flightPrice || (shouldUseCachedFlightPrice ? cachedFlightPrice : 0)

  const pricingWithFlights = effectiveFlightPrice ? addFlightPrice(pricing, effectiveFlightPrice) : undefined
  const memberPricingWithFlights = (memberPricing && effectiveFlightPrice) ? addFlightPrice(memberPricing, effectiveFlightPrice) : undefined

  const fees = feesToObject(pricing.fees)

  const priceWithoutPropertyFees = pricing.price - (fees.property ?? 0)

  const [deals] = useFlightDeals('all', {
    originAirportCode: flightOrigin?.code,
    destinationAirportCode: flightDestination?.code,
  })
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const { currentRegionCode, currentCurrency } = useContext(GeoContext)

  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && (flightsAvailable || bundledWithFlightsOnly)
  const hasFlightPrice = !!pricingWithFlights
  const missingCharterFlightPrice = !!charterBundleRoute && !flightPrice

  // Get charter flight dates to check if user's checkIn matches available charter dates
  const { departDate: charterDepartDate } = useCharterFlightDates({
    checkInDate: checkIn,
    nights: pkg.duration,
    charterBundleRoute,
    rooms,
    fareClass,
  })

  // Check if user's checkIn matches a valid charter flight date
  const isCheckInMatchingCharterDate = !charterBundleRoute || (!!checkIn && !!charterDepartDate)

  // Only show error when checkIn is selected but flight price fetch failed
  const shouldShowCharterFlightErrorMessage = missingCharterFlightPrice && !flightPriceLoading && !!checkIn && isCheckInMatchingCharterDate
  const canBuyNow = !!checkIn && !!rooms && packageView.fromCalendarInfo && !shouldShowCharterFlightErrorMessage && isCheckInMatchingCharterDate

  // Determine if we should use fallback pricing for charter bundle
  // Use fallback when: no checkIn date selected, OR checkIn doesn't match any charter flight date
  const shouldUseFallbackPricing = useMemo(() => {
    if (!charterBundleRoute) return false
    // Use fallback when no checkIn date is selected, or checkIn doesn't match charter flight dates
    return !checkIn || !isCheckInMatchingCharterDate
  }, [charterBundleRoute, checkIn, isCheckInMatchingCharterDate])

  // Get fallback pricing for charter bundle when no dates are selected
  // Pass rooms from context - the hook has its own fallback for empty rooms
  const { fallbackPricing, fallbackMemberPricing } = useCharterBundleFallbackPricing({
    offer: offer as App.HotelOffer,
    rooms,
    charterBundleRoute,
    fareClass,
    disabled: !shouldUseFallbackPricing,
    multipleRoomBookingActive,
    packageUniqueKey: pkg.uniqueKey,
  })

  // Determine which pricing to display for charter bundle
  const charterPricingToDisplay = useMemo(() => {
    if (!charterBundleRoute) return { pricing, memberPricing, isUsingFallback: false }

    // If we have checkIn that matches charter date and have flight price, use the bundled pricing
    if (checkIn && isCheckInMatchingCharterDate && pricingWithFlights) {
      return {
        pricing: pricingWithFlights,
        memberPricing: memberPricingWithFlights,
        isUsingFallback: false,
      }
    }

    // If no checkIn or checkIn doesn't match charter date, use fallback pricing
    if ((!checkIn || !isCheckInMatchingCharterDate) && fallbackPricing) {
      return {
        pricing: fallbackPricing,
        memberPricing: fallbackMemberPricing,
        isUsingFallback: true,
      }
    }

    // Default to hotel pricing (loading state or error)
    return { pricing, memberPricing, isUsingFallback: false }
  }, [charterBundleRoute, checkIn, isCheckInMatchingCharterDate, pricingWithFlights, memberPricingWithFlights, fallbackPricing, fallbackMemberPricing, pricing, memberPricing])

  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])
  const extraGuestSurchargesPayableAtProperty = !!pkg.roomRate?.extraGuestSurcharges.length && pkg.roomRate?.extraGuestSurcharges.some(surcharge => surcharge?.payableAtProperty)

  const shouldShowDiscountPercent = pricing.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const flightsBundledNoCache = !hasFlightPrice && bundledWithFlightsOnly
  const shouldShowBundledDiscountPercent = !!pricingWithFlights && pricingWithFlights.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const discountPercent = bundledWithFlightsOnly ? pricingWithFlights?.discountPercent : max([pricing.discountPercent, pricingWithFlights?.discountPercent ?? 0])
  const showDynamicPricingUrgencyTag = !!roomsBeforeSurcharge
  const departureTooFarInFuture = useMemo(() => {
    return !!checkInMoment && checkInMoment.diff(moment.utc(), 'days') > FLIGHTS_MAX_LEAD_TIME_DAYS
  }, [checkInMoment])

  const showPriceDetails = !!fees.taxesAndFees || !!fees.property || config.agentHub.isEnabled
  const showMemberPrice = !!memberPricing && memberPricing.price > 0

  const canAddToUserCart = canBuyNow

  const shouldShowDiscount = !offer.isDiscountPillHidden && (shouldShowDiscountPercent || shouldShowBundledDiscountPercent) && pkg.shouldDisplayValue && (bundledWithFlightsOnly ? hasFlightPrice : true)
  const shouldShowValue = pricing.value > pricing.price && pkg.shouldDisplayValue
  const isDepositEnabled = isDepositAvailableOnOffer(offer, currentCurrency, checkInMoment, pricing.price)
  const isIndiaRegion = currentRegionCode === 'IN'
  const isFlexibleCancellation = isFlexibleCancellationPolicy(packageView.package?.roomRate?.cancellationPolicy)

  const roomRateIsReservableForZero = isPackageEligibleForZeroReservation(offer, packageView.package.roomRate?.isReservableForZero, checkInMoment)
  const isReserveForZeroEnabled = config.RESERVE_FOR_ZERO_ENABLED
  const showReserveForZero = (isReserveForZeroEnabled || isSpoofed) && roomRateIsReservableForZero
  const showDepositTag = isDepositEnabled && !isSpoofed
  const showFlexiblePaymentTag = isDepositEnabled && isSpoofed

  const showFlightsDealTag = flightsEnabled && flightOrigin && flightDestination && deals.length > 0 && !charterBundleRoute

  const activeDeal = useMemo(
    () => (deals.length > 0 ? sortBy(deals, (d: App.FlightDeal) => d.perAdultPrice, 'asc')[0] : undefined),
    [deals],
  )

  // Hide the regular flights block when charter bundle is using fallback pricing
  const hideHotelAndFlightsPriceBlock = !isFlightsSelected || missingCharterFlightPrice || (!!charterBundleRoute && shouldUseFallbackPricing)
  const flightsBookable = flightOrigin && flightOrigin.code !== flightDestination?.code && !departureTooFarInFuture
  const showModal = useContext(ModalContext)
  const onSubmit = useCallback(() => {
    if (!canBuyNow) {
      const currentPrice = isFlightsSelected ? pricingWithFlights?.price : pricing.price

      if (currentPrice) {
        Analytics.trackEvent(offerPageCheckAvailabilityEvent({ offer, roomRateId: pkg.roomRate?.id, includesFlights: isFlightsSelected, packageOptionPrice: currentPrice }))
      }
    } else {
      trackEvent('BOOK_NOW_PRESSED')
    }
  }, [canBuyNow, isFlightsSelected, offer, pkg.roomRate?.id, pricing.price, pricingWithFlights?.price, trackEvent])

  const onFlexPaymentPolicyOpen = useCallback(async() => {
    showModal(
      <ReserveForZeroAndDepositPolicyModal
        depositAmountPercentage={depositAmountPercentage}
        offerType={offer.type}
        depositThresholds={offer.depositThresholds}
        depositAmount={offer.depositAmount}
      />,
    )
  }, [depositAmountPercentage, offer.depositAmount, offer.depositThresholds, offer.type, showModal])
  const openReserveForZeroPolicyModal = useCallback(() => {
    showModal(<ReserveForZeroPolicyModal />)
  }, [showModal])

  const otherFees = {
    propertyFees: packageView.propertyFees,
    extraGuestsSurcharge: extraGuestSurchargesPayableAtProperty ? packageView.extraGuestsSurcharge : 0,
  }

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode, nights: packageView?.package?.duration }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      flightPrice: effectiveFlightPrice,
      skip: hideHotelAndFlightsPriceBlock,
    }),
  ], [offer, packageView, hideHotelAndFlightsPriceBlock, currentRegionCode, effectiveFlightPrice])

  const isLuxPlusMonthTakeoverEnabled = useCurrentSiteTakeover({ offer })?.id === LUX_PLUS_MONTH_TAKEOVER_ID
  const urgencyLabels = useMemo(() => offer.urgencyTags.filter(tag => tag.type !== 'new'), [offer.urgencyTags])

  const convertToDisplay = getPriceDisplayFunction(priceMode, rooms)

  const totalGuests = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const guestsDisplayText = useMemo(() => {
    if (!charterBundleRoute || !totalGuests) return undefined
    return formatGuestsDisplay(totalGuests)
  }, [charterBundleRoute, totalGuests])

  const hotelOnlyBookNowText = rooms.length > 1
    ? intl.formatMessage(
        {
          id: 'i+C8exSToI',
          defaultMessage: 'Book {saleUnit}',
          description: 'Button text to book multiple items',
        },
        {
          saleUnit: pluralizeToString(offer.saleUnit, rooms.length),
        },
      )
    : intl.formatMessage({
        id: '6UpcMhfTrH',
        defaultMessage: 'Book now',
        description: 'Button text to book now',
      })

  const checkAvailabilityText = intl.formatMessage({
    id: 'SQA+go728o',
    defaultMessage: 'Check availability',
    description: 'Button text to check availability',
  })

  const hotelBookButtonText = canBuyNow ? hotelOnlyBookNowText : checkAvailabilityText

  const bookNowText = intl.formatMessage({
    id: '6UpcMhfTrH',
    defaultMessage: 'Book now',
    description: 'Button text to book now',
  })

  const flightsBookButtonText = canBuyNow ? bookNowText : checkAvailabilityText

  const selectAirlinesMessage = isIndiaRegion
    ? intl.formatMessage({
        id: '6501dFMGSH',
        defaultMessage: 'Select from the best available airlines at the next step.',
        description: 'Message for India region about selecting airlines',
      })
    : intl.formatMessage({
        id: 'At2Utnz5uF',
        defaultMessage: 'Select from all major airlines at the next step.',
        description: 'Message for non-India regions about selecting airlines',
      })

  let priceContent: ReactNode = null

  if (fetching || (isFlightsSelected || charterBundleRoute) && flightPriceLoading) {
    // WAIT FOR PRICES
  } else if (charterBundleRoute && missingCharterFlightPrice) {
    // NO PRICE TO SHOW
  } else if (charterBundleRoute && charterPricingToDisplay.pricing && shouldUseFallbackPricing) {
    // CHARTER BUNDLE FALLBACK PRICE
    priceContent = <VerticalSpacer gap={12} data-testid="charter-pricing">
      <VerticalSpacer gap={4}>
        {isLuxPlusMonthTakeoverEnabled && <OfferLabels
          offer={offer}
          saveAmount={charterPricingToDisplay.memberPricing ? charterPricingToDisplay.pricing.price - charterPricingToDisplay.memberPricing.price : undefined}
          urgencyLabels={urgencyLabels}
        />}
        <AgentHubAdditionalCommissionLabel offerId={offer.id} packageId={pkg.id} />
        <div>
          {!isStoreMode && isSpoofed && !charterPricingToDisplay.isUsingFallback && <FlashHotelMarginText
            checkIn={checkInMoment?.format(ISO_DATE_FORMAT)}
            pkg={pkg}
            type="exact"
          />}

          <PriceRowPriceCaption startIcon={<LinePlaneDepartureIcon />}>
            {pluralizeToString('night', pkg.duration)} & flights {charterPricingToDisplay.isUsingFallback && 'from'}
          </PriceRowPriceCaption>
          <LuxPlusPriceStack
            size="L"
            price={convertToDisplay(charterPricingToDisplay.pricing.price)}
            memberPrice={charterPricingToDisplay.memberPricing?.price && convertToDisplay(charterPricingToDisplay.memberPricing.price)}
            saleUnit={priceMode === 'perPerson' ? 'person' : 'total'}
            testid="offer-package-price"
            offerConfig={offer.luxPlus}
          />
          <PriceRowTaxesAndFees />
          {showIncludedGuestsExceeded && <AdditionalGuestsPopup
            complex={false}
            description={intl.formatMessage({
              id: 'UekksioAOK',
              defaultMessage: '+ Extra guest surcharge',
              description: 'Description for extra guest surcharge',
            })}
            modalContent={pkg.roomPolicyDescription}
          />}
          {charterPricingToDisplay.pricing.value > charterPricingToDisplay.pricing.price && pkg.shouldDisplayValue && <PriceRowValueDiscountWithCaption
            data-testid="value-box"
            discountPercentage={!offer.isDiscountPillHidden && charterPricingToDisplay.pricing.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE ? charterPricingToDisplay.pricing.discountPercent : undefined}
            size="M"
            originalValue={convertToDisplay(charterPricingToDisplay.pricing.value)}
          />}
          {guestsDisplayText && <PriceRowCaption>{guestsDisplayText}</PriceRowCaption>}
        </div>
        <PriceRowAgentHubCommission
          offerId={offer.id}
          productType={offer.productType}
          kind="plain"
          offerTotal={charterPricingToDisplay.pricing.price - (feesToObject(charterPricingToDisplay.pricing.fees).property ?? 0)}
          packageId={pkg.id}
        />
        <div>
          {showPriceDetails && <OfferPriceDetailsRow
            trigger="price-row"
            triggerSize="M"
            offerPage
            offer={offer}
            duration={pkg.duration}
            propertyFees={feesToObject(charterPricingToDisplay.pricing.fees).property}
            hotelPrice={charterPricingToDisplay.pricing.price - (feesToObject(charterPricingToDisplay.pricing.fees).property ?? 0)}
            otherFees={otherFees}
            rooms={rooms}
            extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
            dueAtPropertyMessage={offer.property?.taxesAndFeesContent ?? ''}
            hotelMemberPrice={charterPricingToDisplay.memberPricing?.price}
            showMemberPrice={!!charterPricingToDisplay.memberPricing && charterPricingToDisplay.memberPricing.price > 0}
            cta={<FlexAutoGroup direction="horizontal-reverse" wrap="wrap" tabletDirection="vertical" gap={8} noDisplayWhenEmpty>
              <OfferBookButton
                size="medium"
                type="submit"
                disabled={fetching}
                packageView={packageView}
                onSubmit={onSubmit}
              >
                {hotelBookButtonText}
              </OfferBookButton>
              {canAddToUserCart && <OfferAddToCartButton
                size="medium"
                type="submit"
                disabled={fetching}
                packageView={packageView}
              />}
            </FlexAutoGroup>}
          />}
          {showReserveForZero && <TextLink
            colour="highlight-secondary"
            size="small"
            weight="bold"
            endIcon={<LineInfoCircleIcon colour="primary" />}
            onClick={openReserveForZeroPolicyModal}
          >
            {intl.formatMessage({
              id: 'Qtjnxg++0D',
              defaultMessage: 'Reserve now for free',
              description: 'Link text to reserve now for free',
            })}
          </TextLink>}
        </div>
      </VerticalSpacer>
      <LoyaltyPointsEarnWithBreakdownModal
        calculationRequests={pointsEarnCalculationRequests}
        calculationType={charterPricingToDisplay.isUsingFallback ? 'estimate' : 'exact'}
        itemTitle={packageView.package.roomType.name}
      />
    </VerticalSpacer>
  } else if ((isFlightsSelected || charterBundleRoute) && flightsEnabled) {
    // FLIGHT PRICE
    priceContent = <VerticalSpacer gap={12} data-testid="flights-pricing">
      {showFlightsDealTag && activeDeal && <FlightDealLabel activeDeal={activeDeal} showCountdown={false} />}
      <div>
        {hasFlightPrice && <>
          {checkIn && <PriceRowPriceCaption>
            <LinePlaneDepartureIcon size="S" /> {pluralizeToString('night', pkg.duration)} & flights
          </PriceRowPriceCaption>}
          {!checkIn && <PriceRowPriceCaption>
            <LinePlaneDepartureIcon size="S" /> {pluralizeToString('night', pkg.duration)} & flights from
          </PriceRowPriceCaption>}
          <LuxPlusPriceStack
            size="L"
            price={convertToDisplay(pricingWithFlights.price)}
            memberPrice={memberPricingWithFlights?.price && convertToDisplay(memberPricingWithFlights?.price)}
            saleUnit={priceMode === 'perPerson' ? 'person*' : 'total*'}
            testid="offer-package-price-with-flight"
            offerConfig={offer.luxPlus}
          />
          {!rooms && <PriceRowCaption>
            {intl.formatMessage({
              id: 'jFlcLR1EAh',
              defaultMessage: 'Two Adults',
              description: 'Caption showing default occupancy of two adults',
            })}
          </PriceRowCaption>}
          <PriceRowTaxesAndFees />
          {showIncludedGuestsExceeded && <AdditionalGuestsPopup
            complex={false}
            description={intl.formatMessage({
              id: 'UekksioAOK',
              defaultMessage: '+ Extra guest surcharge',
              description: 'Description for extra guest surcharge',
            })}
            modalContent={pkg.roomPolicyDescription}
          />}
          {shouldShowValue && <PriceRowValueDiscountWithCaption
            data-testid="value-box"
            size="M"
            originalValue={convertToDisplay(pricingWithFlights.value)}
          />}
          {guestsDisplayText && <PriceRowCaption>{guestsDisplayText}</PriceRowCaption>}

          <PriceRowAgentHubCommission
            kind="plain"
            offerId={offer.id}
            productType={offer.productType}
            offerLocations={offerLocations}
            vendorName={vendorName}
            offerTotal={pricingWithFlights.price}
            packageId={pkg.id}
          />
        </>}

        {!hasFlightPrice && <>
          {flightOrigin.code === flightDestination?.code && <PriceRowCaption>
            {intl.formatMessage(
              {
                id: 'ui/0SxB+9L',
                defaultMessage: 'No flights available from {airportName}. Please select another departing airport.',
                description: 'Message when no flights available from selected airport',
              },
              {
                airportName: flightOrigin.name,
              },
            )}
          </PriceRowCaption>}
          {departureTooFarInFuture && <PriceRowCaption>
            {intl.formatMessage({
              id: 'cds7S7OB+9',
              defaultMessage: 'Flights can only be booked up to 11 months in advance.',
              description: 'Message when departure date is too far in future',
            })}
          </PriceRowCaption>}

          {checkIn && <PriceRowPriceCaption>
            <LinePlaneDepartureIcon size="S" /> {pluralizeToString('night', pkg.duration)}
          </PriceRowPriceCaption>}
          {!checkIn && <PriceRowPriceCaption>
            <LinePlaneDepartureIcon size="S" /> {pluralizeToString('night', pkg.duration)} from
          </PriceRowPriceCaption>}
          <LuxPlusPriceStack
            size="L"
            price={convertToDisplay(pricing.price)}
            memberPrice={memberPricing?.price && convertToDisplay(memberPricing?.price)}
            saleUnit={priceMode === 'perPerson' ? 'person' : 'total'}
            testid="offer-package-price-with-flight"
            offerConfig={offer.luxPlus}
          />
          {!rooms && <PriceRowCaption>
            {intl.formatMessage({
              id: 'jFlcLR1EAh',
              defaultMessage: 'Two Adults',
              description: 'Caption showing default occupancy of two adults',
            })}
          </PriceRowCaption>}
          <Caption variant="large">
            {intl.formatMessage({
              id: 'raeN2bwirm',
              defaultMessage: '& flights',
              description: 'Caption indicating flights are included',
            })}
          </Caption>
          {showIncludedGuestsExceeded && <AdditionalGuestsPopup
            complex={false}
            description={intl.formatMessage({
              id: 'UekksioAOK',
              defaultMessage: '+ Extra guest surcharge',
              description: 'Description for extra guest surcharge',
            })}
            modalContent={pkg.roomPolicyDescription}
          />}
          {shouldShowValue && <PriceRowValueDiscountWithCaption
            data-testid="value-box"
            size="M"
            originalValue={convertToDisplay(pricing.value)}
          />}
          {guestsDisplayText && <PriceRowCaption>{guestsDisplayText}</PriceRowCaption>}

          <PriceRowAgentHubCommission
            kind="plain"
            offerId={offer.id}
            productType={offer.productType}
            offerLocations={offerLocations}
            vendorName={vendorName}
            offerTotal={pricing.price}
            packageId={pkg.id}
          />

          {!flightsBundledNoCache && flightsBookable && <PriceRowCaption>
            {selectAirlinesMessage}
          </PriceRowCaption>}
        </>}

        {showPriceDetails && <OfferPriceDetailsRow
          trigger="price-row"
          triggerSize="M"
          offerPage
          offer={offer}
          duration={pkg.duration}
          propertyFees={packageView.propertyFees}
          otherFees={otherFees}
          hotelPrice={packageView.hotelPrice}
          flightPrice={effectiveFlightPrice}
          rooms={rooms}
          extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
          dueAtPropertyMessage={offer.property?.taxesAndFeesContent ?? ''}
          hotelMemberPrice={packageView.hotelMemberPrice}
          showMemberPrice={showMemberPrice}
          cta={<Group
            direction="vertical"
            tabletDirection="horizontal"
            horizontalAlign="stretch"
            tabletHorizontalAlign="end"
            gap={8}
          >
            {canAddToUserCart && !!charterBundleRoute && <OfferAddToCartButton
              size="medium"
              type="submit"
              disabled={fetching}
              packageView={packageView}
              data-buytype="flights"
            />}
            {canAddToUserCart && !charterBundleRoute && <OfferSelectPackageButton
              size="medium"
              type="submit"
              disabled={fetching}
              packageView={packageView}
              data-buytype="flights"
              isCharterBundle={false}
            />}
            {(!canBuyNow || !!charterBundleRoute) && <OfferBookButton
              size="medium"
              type="submit"
              disabled={fetching}
              packageView={packageView}
              data-buytype="flights"
              onSubmit={onSubmit}
            >
              {flightsBookButtonText}
            </OfferBookButton>}
          </Group>}
        />}

        <AtolProtectedTooltip />
      </div>

      <LoyaltyPointsEarnWithBreakdownModal
        calculationRequests={pointsEarnCalculationRequests}
        calculationType={(!checkIn || isFlightsSelected) ? 'estimate' : 'exact'}
        itemTitle={packageView.package.roomType.name}
      />
    </VerticalSpacer>
  } else {
    // HOTEL PRICE
    priceContent = <VerticalSpacer gap={12} data-testid="pricing">
      <VerticalSpacer gap={4}>
        {isLuxPlusMonthTakeoverEnabled && <OfferLabels offer={offer} saveAmount={showMemberPrice ? pricing.price - memberPricing?.price : undefined} urgencyLabels={urgencyLabels} />}
        <AgentHubAdditionalCommissionLabel offerId={offer.id} packageId={pkg.id} />
        <div>
          {!isStoreMode && isSpoofed && <FlashHotelMarginText
            checkIn={checkInMoment?.format(ISO_DATE_FORMAT)}
            pkg={pkg}
            type="exact"
          />}

          <PriceRowPriceCaption>
            {pluralizeToString('night', pkg.duration)} {!checkIn && 'from'}
          </PriceRowPriceCaption>
          <LuxPlusPriceStack
            size="L"
            price={convertToDisplay(pricing.price)}
            memberPrice={memberPricing?.price && convertToDisplay(memberPricing?.price)}
            saleUnit={priceMode === 'perPerson' ? 'person' : 'total'}
            testid="offer-package-price"
            offerConfig={offer.luxPlus}
          />
          <PriceRowTaxesAndFees />
          {showIncludedGuestsExceeded && <AdditionalGuestsPopup
            complex={false}
            description={intl.formatMessage({
              id: 'UekksioAOK',
              defaultMessage: '+ Extra guest surcharge',
              description: 'Description for extra guest surcharge',
            })}
            modalContent={pkg.roomPolicyDescription}
          />}
          {shouldShowValue && <PriceRowValueDiscountWithCaption
            data-testid="value-box"
            discountPercentage={shouldShowDiscount && showDynamicPricingUrgencyTag ? discountPercent : undefined}
            size="M"
            originalValue={convertToDisplay(pricing.value)}
          />}
          {guestsDisplayText && <PriceRowCaption>{guestsDisplayText}</PriceRowCaption>}
        </div>
        <PriceRowAgentHubCommission
          offerId={offer.id}
          productType={offer.productType}
          kind="plain"
          offerTotal={priceWithoutPropertyFees}
          packageId={pkg.id}
        />
        <div>
          {showPriceDetails && <OfferPriceDetailsRow
            trigger="price-row"
            triggerSize="M"
            offerPage
            offer={offer}
            duration={pkg.duration}
            propertyFees={fees.property}
            hotelPrice={priceWithoutPropertyFees}
            otherFees={otherFees}
            rooms={rooms}
            extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
            dueAtPropertyMessage={offer.property?.taxesAndFeesContent ?? ''}
            hotelMemberPrice={packageView.hotelMemberPrice}
            showMemberPrice={showMemberPrice}
            cta={<FlexAutoGroup direction="horizontal-reverse" wrap="wrap" tabletDirection="vertical" gap={8} noDisplayWhenEmpty>
              <OfferBookButton
                size="medium"
                type="submit"
                disabled={fetching}
                packageView={packageView}
                onSubmit={onSubmit}
              >
                {hotelBookButtonText}
              </OfferBookButton>
              {canAddToUserCart && <OfferAddToCartButton
                size="medium"
                type="submit"
                disabled={fetching}
                packageView={packageView}
              />}
            </FlexAutoGroup>}
          />}
          {showReserveForZero && <TextLink
            colour="highlight-secondary"
            size="small"
            weight="bold"
            endIcon={<LineInfoCircleIcon colour="primary" />}
            onClick={openReserveForZeroPolicyModal}
          >
            {intl.formatMessage({
              id: 'Qtjnxg++0D',
              defaultMessage: 'Reserve now for free',
              description: 'Link text to reserve now for free',
            })}
          </TextLink>}
        </div>
      </VerticalSpacer>
      <LoyaltyPointsEarnWithBreakdownModal
        calculationRequests={pointsEarnCalculationRequests}
        calculationType={(!checkIn || isFlightsSelected) ? 'estimate' : 'exact'}
        itemTitle={packageView.package.roomType.name}
      />
    </VerticalSpacer>
  }

  return <>
    <Group direction="horizontal" gap={8} wrap="wrap" noDisplayWhenEmpty>
      {shouldShowDiscount && !showDynamicPricingUrgencyTag && !!discountPercent && <PriceRowValueDiscount
        type="estimate"
        discountPercentage={discountPercent}
      />}
      {showDynamicPricingUrgencyTag && !!roomsBeforeSurcharge &&
        <UrgencyLabel variant="critical2">
          {intl.formatMessage(
            {
              id: 'kGkN2IDJMX',
              defaultMessage: '{count} left at this price',
              description: 'Urgency label showing rooms left at current price',
            },
            {
              count: roomsBeforeSurcharge,
            },
          )}
        </UrgencyLabel>}
      {showDepositTag && <SecureWithDepositTagAndModal
        isFlexibleCancellation={isFlexibleCancellation}
        offerType={offer.type}
        depositAmountPercentage={depositAmountPercentage}
        depositThresholds={offer.depositThresholds}
      />}
      {showFlexiblePaymentTag && <Clickable onClick={onFlexPaymentPolicyOpen}>
        <Pill startIcon={<LineClockDollarIcon />} kind="tertiary" variant="primary">
          {intl.formatMessage({
            id: 'BGTFLVcrmg',
            defaultMessage: 'Flexible payment options',
            description: 'Label for flexible payment options',
          })}
        </Pill>
      </Clickable>}
    </Group>
    {priceContent}
  </>
}

export default withOfferPageAndReduxState((state, appState, ownProps: Props) => ({
  rooms: state.rooms,
  offer: state.offer,
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  fetching: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey],
  isSpoofed: appState.auth.account.isSpoofed,
  isStoreMode: appState.system.storeMode,
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  isFlightsSelected: getEffectiveCharterBundleRoute(state) ? true : state.flights, // enforce to be true when charter bundle is selected to avoid unexpected behavior
  open: !!state.activeFilter,
  charterBundleRoute: getEffectiveCharterBundleRoute(state),
  priceMode: state.priceMode,
  flightDestination: getOfferDestinationAirport(state, appState),
  fareClass: state.fareClass,
  multipleRoomBookingActive: isMultipleRoomBookingActive(state, appState),
  bundledWithFlightsOnly: getEffectiveBundledWithFlightsOnly(state),
}), FlashHotelOptionPriceDetails)
